// src/components/admin/Settings.jsx
import React, { useEffect, useState } from "react";
import { Layout, Card, Form, Input, Button, Typography, message, Spin } from "antd";
import AdminHeader from "./AdminHeader";

const { Content } = Layout;
const { Title, Paragraph } = Typography;

/**
 * Settings allows admin to update their display name and change password.
 * Backend endpoints expected:
 *  - GET  /admin_profile.php         -> { ok: true, profile: { name, email } }
 *  - POST /admin_update_profile.php  -> { ok: true, profile: {...} }
 *  - POST /admin_change_password.php -> { ok: true }
 *
 * Adjust endpoints as your backend expects.
 */
export default function Settings() {
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [pwdForm] = Form.useForm();

  const apiBase = (
  window.__APP_CONFIG__?.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:8000"
).replace(/\/$/, ""); // remove trailing slash if any
  const profileEndpoint = apiBase + "/admin_profile.php";
  const updateProfileEndpoint = apiBase + "/admin_update_profile.php";
  const changePasswordEndpoint = apiBase + "/admin_change_password.php";

  const token = localStorage.getItem("admin_token") || null;

  useEffect(() => {
    const fetchProfile = async () => {
      setProfileLoading(true);
      try {
        const res = await fetch(profileEndpoint, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: token ? `Bearer ${token}` : undefined },
        });
        const data = await res.json();
        if (!res.ok || !data.ok) throw new Error(data.error || `Failed to fetch profile (status ${res.status})`);
        setProfile(data.profile || {});
        form.setFieldsValue({ name: data.profile?.name || "", email: data.profile?.email || "" });
      } catch (err) {
        console.error("fetch profile error", err);
        messageApi.error(err?.message || "Failed to load profile");
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProfileSave = async (values) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", values.name);
      // don't allow email change here by default; backend can ignore
      const res = await fetch(updateProfileEndpoint, {
        method: "POST",
        body: fd,
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to update profile");
      messageApi.success("Profile updated");
      // update local storage display name if backend returns it
      if (data.profile?.name) localStorage.setItem("admin_name", data.profile.name);
      setProfile(data.profile || profile);
    } catch (err) {
      console.error("update profile error", err);
      messageApi.error(err?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values) => {
    setLoading(true);
    try {
      if (values.newPassword !== values.confirmPassword) {
        throw new Error("New password and confirm password do not match");
      }
      const fd = new FormData();
      fd.append("current_password", values.currentPassword);
      fd.append("new_password", values.newPassword);

      const res = await fetch(changePasswordEndpoint, {
        method: "POST",
        body: fd,
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to change password");
      messageApi.success("Password changed successfully");
      pwdForm.resetFields();
    } catch (err) {
      console.error("change password error", err);
      messageApi.error(err?.message || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f6fa" }}>
      <AdminHeader />
      <Content style={{ padding: "24px 40px" }}>
        {contextHolder}
        <Title level={4} style={{ marginBottom: 8 }}>Admin Settings</Title>
        <Paragraph style={{ marginTop: 0, marginBottom: 20, color: "#6b7280" }}>
          Update your display name and change your password here.
        </Paragraph>

        <Spin spinning={profileLoading}>
          <Card style={{ marginBottom: 20 }} bordered>
            <Title level={5}>Profile</Title>
            <Form form={form} layout="vertical" onFinish={handleProfileSave} initialValues={{ name: profile?.name || "" }}>
              <Form.Item name="name" label="Display name" rules={[{ required: true, message: "Enter display name" }]}>
                <Input />
              </Form.Item>
              <Form.Item name="email" label="Email (read-only)">
                <Input disabled />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Save Profile
                </Button>
              </Form.Item>
            </Form>
          </Card>

          <Card bordered>
            <Title level={5}>Change Password</Title>
            <Form form={pwdForm} layout="vertical" onFinish={handleChangePassword}>
              <Form.Item name="currentPassword" label="Current password" rules={[{ required: true }]}>
                <Input.Password />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label="New password"
                rules={[{ required: true, min: 6, message: "Enter a new password (min 6 chars)" }]}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm new password"
                rules={[{ required: true, min: 6 }]}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Change Password
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Spin>
      </Content>
    </Layout>
  );
}
